import { FC, useMemo } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'
import theme from 'theme'
import { Application, ApplicationManifest } from 'models'
import { useUsersLookup } from 'hooks/useUsersLookup'
import {
  ActionsWrapper,
  Drawer,
  InstancesWrapper,
  IntegrationInstanceItem,
  MetaDataItem,
  MetaDataWrapper
} from '../../../addInstanceDrawer.styles'
import { CategoryChip } from '../../../components/integrationCard.styles'
import ThemeButton from '../../../../../../components/buttons/primary/ThemeButton'
import { PermissionTooltip } from '../../../../../../components/tooltip'
import { CatalogInstance } from '../useApplicationsCatalog'
import {
  ABOUT_INSTANCE_TITLE,
  ABOUT_INTEGRATION_TITLE,
  ADD_INSTANCE_LABEL,
  CONFIGURED_BY_LABEL,
  CREATED_BY_LABEL,
  DATE_CREATED_LABEL,
  DATE_EDITED_LABEL,
  INSTANCES_TAB_LABEL,
  LATEST_EDIT_BY_LABEL,
  META_DATA_TITLE,
  READ_ONLY_TOOLTIP,
  REMOVE_LABEL,
  VIEW_ALL_LABEL
} from '../applicationsCatalog.constants'

const SHOWN_INSTANCES = 2

const formatDate = (date?: string | null) => dayjs(date).format('MM/DD/YYYY')

const ConfiguredByIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M11.8126 4.5C11.8126 6.0533 10.5534 7.3125 9.00013 7.3125C7.44682 7.3125 6.18763 6.0533 6.18763 4.5C6.18763 2.9467 7.44682 1.6875 9.00013 1.6875C10.5534 1.6875 11.8126 2.9467 11.8126 4.5Z"
      stroke={theme.colors.textCaption}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.37598 15.0887C3.42871 12.0277 5.92653 9.5625 9.00013 9.5625C12.0738 9.5625 14.5717 12.0278 14.6243 15.0889C12.9122 15.8745 11.0074 16.3125 9.00037 16.3125C6.99312 16.3125 5.0882 15.8744 3.37598 15.0887Z"
      stroke={theme.colors.textCaption}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const DateCreatedIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
    <path
      d="M3.06579 0.5V2.03947M10.25 0.5V2.03947M0.5 11.2763V3.57895C0.5 2.72872 1.18925 2.03947 2.03947 2.03947H11.2763C12.1265 2.03947 12.8158 2.72872 12.8158 3.57895V6.14474M0.5 11.2763C0.5 12.1265 1.18925 12.8158 2.03947 12.8158H7.3421M0.5 11.2763V6.14474C0.5 5.29451 1.18925 4.60526 2.03947 4.60526H11.2763C12.1265 4.60526 12.8158 5.29451 12.8158 6.14474M12.8158 6.14474V7.3421M14.2539 10.3366H13.0057M14.2539 10.3366V9.08846M14.2539 10.3366L13.4586 9.54089C13.211 9.29279 12.8967 9.10456 12.5338 9.00731C11.4335 8.7125 10.3026 9.36545 10.0077 10.4657M9.74609 12.9106V11.6624M9.74609 11.6624H10.9943M9.74609 11.6624L10.5413 12.4582C10.7889 12.7063 11.1032 12.8945 11.4662 12.9918C12.5664 13.2866 13.6974 12.6336 13.9922 11.5333"
      stroke={theme.colors.textCaption}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const MetaCreatedIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M5.08278 2.5V4.03947M12.3146 2.5V4.03947M2.5 13.2763V5.57895C2.5 4.72872 3.19381 4.03947 4.04967 4.03947H13.3477C14.2035 4.03947 14.8974 4.72872 14.8974 5.57895V8.14474M2.5 13.2763C2.5 14.1265 3.19381 14.8158 4.04967 14.8158H9.38742M2.5 13.2763V8.14474C2.5 7.29451 3.19381 6.60526 4.04967 6.60526H13.3477C14.2035 6.60526 14.8974 7.29451 14.8974 8.14474M14.8974 8.14474V9.34211M10.851 13.1908H13.1755M13.1755 13.1908H15.5M13.1755 13.1908V10.8816M13.1755 13.1908V15.5"
      stroke={theme.colors.textCaption}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const MetaCreatedByIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M13.5 5.625V7.875M13.5 7.875V10.125M13.5 7.875H15.75M13.5 7.875H11.25M9.5625 4.78125C9.5625 6.17922 8.42922 7.3125 7.03125 7.3125C5.63328 7.3125 4.5 6.17922 4.5 4.78125C4.5 3.38328 5.63328 2.25 7.03125 2.25C8.42922 2.25 9.5625 3.38328 9.5625 4.78125ZM2.25069 14.4257C2.25023 14.3985 2.25 14.3711 2.25 14.3438C2.25 11.7031 4.39064 9.5625 7.03125 9.5625C9.67186 9.5625 11.8125 11.7031 11.8125 14.3438V14.3457C11.8125 14.3724 11.8123 14.3991 11.8118 14.4257C10.4155 15.2664 8.77986 15.75 7.03125 15.75C5.28264 15.75 3.64698 15.2664 2.25069 14.4257Z"
      stroke={theme.colors.textCaption}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const MetaEditedIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M3.06579 0.5V2.03947M10.25 0.5V2.03947M0.5 11.2763V3.57895C0.5 2.72872 1.18925 2.03947 2.03947 2.03947H11.2763C12.1265 2.03947 12.8158 2.72872 12.8158 3.57895V6.14474M0.5 11.2763C0.5 12.1265 1.18925 12.8158 2.03947 12.8158H7.3421M0.5 11.2763V6.14474C0.5 5.29451 1.18925 4.60526 2.03947 4.60526H11.2763C12.1265 4.60526 12.8158 5.29451 12.8158 6.14474M12.8158 6.14474V7.3421"
      stroke={theme.colors.textCaption}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.3246 8.88629C13.0908 8.65249 12.7118 8.65249 12.478 8.88629L12.2141 9.1502L13.0607 9.99687L13.3246 9.73295C13.5584 9.49915 13.5584 9.12009 13.3246 8.88629Z"
      fill={theme.colors.textCaption}
    />
    <path
      d="M12.8188 10.2388L11.9721 9.39211L9.20091 12.1633C9.06024 12.304 8.95683 12.4775 8.90004 12.6682L8.71763 13.2805C8.6997 13.3407 8.7162 13.4059 8.76061 13.4503C8.80503 13.4947 8.87021 13.5112 8.9304 13.4933L9.54274 13.3109C9.7334 13.2541 9.90691 13.1507 10.0476 13.01L12.8188 10.2388Z"
      fill={theme.colors.textCaption}
    />
  </svg>
)

const MetaEditedByIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M9.5625 4.78125C9.5625 6.17922 8.42922 7.3125 7.03125 7.3125C5.63328 7.3125 4.5 6.17922 4.5 4.78125C4.5 3.38328 5.63328 2.25 7.03125 2.25C8.42922 2.25 9.5625 3.38328 9.5625 4.78125Z"
      stroke={theme.colors.textCaption}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.25069 14.4257C2.25023 14.3985 2.25 14.3711 2.25 14.3438C2.25 11.7031 4.39064 9.5625 7.03125 9.5625C9.67186 9.5625 11.8125 11.7031 11.8125 14.3438V14.3457C11.8125 14.3724 11.8123 14.3991 11.8118 14.4257C10.4155 15.2664 8.77986 15.75 7.03125 15.75C5.28264 15.75 3.64698 15.2664 2.25069 14.4257Z"
      stroke={theme.colors.textCaption}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.7986 3.70136C16.5302 3.43288 16.0949 3.43288 15.8264 3.70136L15.5233 4.00443L16.4956 4.9767L16.7986 4.67364C17.0671 4.40515 17.0671 3.96985 16.7986 3.70136Z"
      fill={theme.colors.textCaption}
    />
    <path
      d="M16.2178 5.25449L15.2455 4.28222L12.0631 7.46458C11.9016 7.62612 11.7829 7.82536 11.7176 8.04431L11.5082 8.74749C11.4876 8.81661 11.5065 8.89146 11.5575 8.94246C11.6085 8.99346 11.6834 9.01241 11.7525 8.99182L12.4557 8.78235C12.6746 8.71713 12.8739 8.59839 13.0354 8.43685L16.2178 5.25449Z"
      fill={theme.colors.textCaption}
    />
  </svg>
)

const RemoveIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
    <path
      d="M10.3269 6L10.0962 12M6.90385 12L6.67308 6M13.3184 3.86038C13.5464 3.89481 13.7736 3.93165 14 3.97086M13.3184 3.86038L12.6065 13.115C12.5464 13.8965 11.8948 14.5 11.111 14.5H5.88905C5.10524 14.5 4.45358 13.8965 4.39346 13.115L3.68157 3.86038M13.3184 3.86038C12.5542 3.74496 11.781 3.65657 11 3.59622M3 3.97086C3.22638 3.93165 3.45358 3.89481 3.68157 3.86038M3.68157 3.86038C4.44585 3.74496 5.219 3.65657 6 3.59622M11 3.59622V2.98546C11 2.19922 10.3929 1.54282 9.60706 1.51768C9.23948 1.50592 8.87043 1.5 8.5 1.5C8.12957 1.5 7.76052 1.50592 7.39294 1.51768C6.6071 1.54282 6 2.19922 6 2.98546V3.59622M11 3.59622C10.175 3.53247 9.34131 3.5 8.5 3.5C7.65869 3.5 6.82496 3.53247 6 3.59622"
      stroke={theme.colors.menuItemText}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const REMOVE_HOVER_SX = {
  '&:hover, &:focus, &:active': {
    svg: {
      path: {
        fill: 'unset !important',
        stroke: `${theme.colors.cta} !important`
      }
    }
  }
}

interface Props {
  open: boolean
  manifest: ApplicationManifest | null
  /* Set when the pane shows one instance rather than its manifest. */
  application: Application | null
  /* The manifest's live applications, newest first. */
  instances: Application[]
  noEditPermission: boolean
  onClose: () => void
  onViewInstances: (manifest: ApplicationManifest) => void
  onAddInstance: (manifest: ApplicationManifest) => void
  onRemove: (instance: CatalogInstance) => void
}

const ApplicationDrawer: FC<Props> = ({
  open,
  manifest,
  application,
  instances,
  noEditPermission,
  onClose,
  onViewInstances,
  onAddInstance,
  onRemove
}) => {
  /* The list answers carry ids, not names, so the members behind them are resolved here. */
  const memberIds = useMemo(
    () =>
      Array.from(
        new Set(
          [...instances.map((i) => i.created_by), application?.created_by, application?.updated_by].filter(
            Boolean
          ) as string[]
        )
      ),
    [instances, application]
  )
  const { getUserLabel } = useUsersLookup(memberIds)

  if (!manifest) return null

  const memberName = (id?: string | null, resolved?: string | null) => resolved?.trim() || getUserLabel(id)

  const shown = instances.slice(0, SHOWN_INSTANCES)
  const hasMore = instances.length > SHOWN_INSTANCES

  const renderBlock = (icon: JSX.Element, title: string, text: string) => (
    <Box className="instance-block-item">
      <Box className="instance-block-icon-wrapper">{icon}</Box>
      <Box className="instance-block-content-wrapper">
        <Typography className="instance-block-content-title">{title}</Typography>
        <Typography className="instance-block-content-text">{text}</Typography>
      </Box>
    </Box>
  )

  const renderMetaBlock = (icon: JSX.Element, title: string, text: string) => (
    <Box className="metadata-block-item">
      <Box className="metadata-block-icon-wrapper">{icon}</Box>
      <Box className="metadata-block-content-wrapper">
        <Typography className="metadata-block-content-title">{title}</Typography>
        <Typography className="metadata-block-content-text">{text}</Typography>
      </Box>
    </Box>
  )

  const renderManifestInstances = () =>
    shown.length > 0 && (
      <InstancesWrapper>
        <Box className="instances-inner">
          <Typography className="instances-title">
            {INSTANCES_TAB_LABEL} ({instances.length})
          </Typography>

          {shown.map((instance, i) => {
            /* A divider separates each item from what follows it, the View all button included. */
            const divided = i < shown.length - 1 || hasMore
            return (
              <IntegrationInstanceItem
                key={instance.id}
                className="integration-instance-item"
                sx={{
                  borderBottom: divided ? `1px solid ${theme.colors.borderMedium}` : '',
                  paddingBottom: divided ? `${theme.spacing.small}px` : '0'
                }}
              >
                <Typography className="instances-instance-name">{instance.display_name}</Typography>

                <Box className="instance-blocks-list">
                  {renderBlock(
                    ConfiguredByIcon,
                    CONFIGURED_BY_LABEL,
                    memberName(instance.created_by, instance.created_by_name)
                  )}
                  {renderBlock(DateCreatedIcon, DATE_CREATED_LABEL, formatDate(instance.created_at))}
                </Box>
              </IntegrationInstanceItem>
            )
          })}

          {hasMore && (
            <ThemeButton themevariant="hollow" onClick={() => onViewInstances(manifest)}>
              {VIEW_ALL_LABEL}
            </ThemeButton>
          )}
        </Box>
      </InstancesWrapper>
    )

  const renderMetaData = (instance: Application) => (
    <MetaDataWrapper>
      <Box className="metadata-inner">
        <Typography className="metadata-title">{META_DATA_TITLE}</Typography>

        <MetaDataItem className="metadata-instance-item">
          <Box className="metadata-blocks-list">
            {renderMetaBlock(MetaCreatedIcon, DATE_CREATED_LABEL, formatDate(instance.created_at))}
            {renderMetaBlock(
              MetaCreatedByIcon,
              CREATED_BY_LABEL,
              memberName(instance.created_by, instance.created_by_name)
            )}
            {instance.updated_at && renderMetaBlock(MetaEditedIcon, DATE_EDITED_LABEL, formatDate(instance.updated_at))}
            {/* A never-edited instance has no editor yet, so its creator made the latest edit. */}
            {renderMetaBlock(
              MetaEditedByIcon,
              LATEST_EDIT_BY_LABEL,
              instance.updated_by
                ? memberName(instance.updated_by, instance.updated_by_name)
                : memberName(instance.created_by, instance.created_by_name)
            )}
          </Box>
        </MetaDataItem>
      </Box>
    </MetaDataWrapper>
  )

  const renderRemoveButton = (instance: Application) => {
    const content = (
      <>
        {RemoveIcon}
        {REMOVE_LABEL}
      </>
    )

    if (noEditPermission) {
      return (
        <PermissionTooltip
          PopperProps={{ sx: { marginTop: `-${theme.spacing.sm}px !important` } }}
          title={
            <Box className="tooltip-wrapper-permissions one-line">
              <Typography className="tooltip-text">{READ_ONLY_TOOLTIP}</Typography>
            </Box>
          }
        >
          <ThemeButton
            themevariant="secondary"
            className="view-instances-button role-disabled"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            sx={REMOVE_HOVER_SX}
          >
            {content}
          </ThemeButton>
        </PermissionTooltip>
      )
    }

    return (
      <ThemeButton
        themevariant="secondary"
        className="view-instances-button delete-button"
        onClick={() => onRemove({ application: instance, manifest })}
        sx={REMOVE_HOVER_SX}
      >
        {content}
      </ThemeButton>
    )
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} integrationtype={application ? 'edit' : 'add'}>
      <Box className="wrapper-inner" role="presentation">
        <Box className="inner-scrollable-section">
          <Box className="header-container">
            <Box className="header">
              <Typography className="title">
                {application ? application.display_name : manifest.display_name}
              </Typography>
              <IconButton className="close-icon" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography className="sub-header-title">{manifest.vendor.display_name}</Typography>
          </Box>

          <Box className="categories-wrapper">
            {manifest.supported_categories.map((category) => (
              <CategoryChip key={category} label={category} size="small" />
            ))}
          </Box>

          <Box className="description-wrapper">
            <Typography className="description-title">
              {application ? ABOUT_INSTANCE_TITLE : ABOUT_INTEGRATION_TITLE}
            </Typography>
            <Typography className="description-text">
              {application ? application.description : manifest.description}
            </Typography>
          </Box>

          {application ? renderMetaData(application) : renderManifestInstances()}
        </Box>

        <ActionsWrapper className="actions-wrapper">
          {application ? (
            renderRemoveButton(application)
          ) : (
            <>
              {instances.length > 0 && (
                <ThemeButton
                  themevariant="secondary"
                  className="view-instances-button"
                  onClick={() => onViewInstances(manifest)}
                >
                  {`View Instance${instances.length === 1 ? '' : 's'}`}
                </ThemeButton>
              )}
              <ThemeButton
                themevariant="primary"
                className="add-instance-button"
                onClick={() => onAddInstance(manifest)}
              >
                {ADD_INSTANCE_LABEL}
              </ThemeButton>
            </>
          )}
        </ActionsWrapper>
      </Box>
    </Drawer>
  )
}

export default ApplicationDrawer
